import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

/**
 * Full-screen, non-dismissible overlay shown to authenticated users whose
 * `terms_accepted_at` predates the current `terms_effective_date`.
 *
 * Mounted globally in App.js — sits ABOVE every route. Customers are excluded.
 *
 * The overlay polls /api/terms/version + reads the latest user record to
 * decide whether to show. On "I Agree" it calls POST /api/users/me/accept-terms
 * which writes an audit_log entry with note "Re-accepted after terms update".
 */
export default function TermsReacceptOverlay() {
  const [show, setShow] = useState(false);
  const [version, setVersion] = useState(null); // { effective_date, effective_at, what_changed }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api.get("/auth/me").catch(() => null);
        const user = me?.data;
        // Only registered users — skip customers / anonymous browsing entirely.
        if (!user?.user_id) return;

        const v = await api.get("/terms/version").then((r) => r.data).catch(() => null);
        if (!v) return;

        const userAcceptedAt = user.terms_accepted_at || "";
        const effectiveAt = v.effective_at || "";
        // If user has never accepted OR their acceptance predates the new effective date.
        const needsAccept = !userAcceptedAt || userAcceptedAt < effectiveAt;
        if (!cancelled && needsAccept) {
          setVersion(v);
          setShow(true);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Lock background scroll while the overlay is mounted.
  useEffect(() => {
    if (!show) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [show]);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/users/me/accept-terms", {});
      setShow(false);
    } catch {
      // swallow — keep overlay open so user can retry
    } finally {
      setBusy(false);
    }
  };

  if (!show || !version) return null;

  return (
    <div
      data-testid="terms-reaccept-overlay"
      className="fixed inset-0 z-[200] flex items-center justify-center px-5"
      style={{ background: "#0B0B0B", overflowY: "auto" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-reaccept-title"
    >
      <div
        className="w-full max-w-md my-8"
        style={{
          background: "#171717",
          border: "1px solid rgba(232,184,75,0.5)",
          borderRadius: 14,
          padding: 28,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <div
          className="text-center"
          style={{ color: "#E8B84B", fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", marginBottom: 10 }}
        >
          Habano · {new Date(version.effective_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
        </div>
        <h2
          id="terms-reaccept-title"
          data-testid="terms-reaccept-title"
          className="text-center"
          style={{ fontFamily: "'Playfair Display', serif", color: "#F5F1E8", fontSize: 28, lineHeight: 1.2 }}
        >
          Our Terms Have Been <em style={{ color: "#E8B84B", fontStyle: "italic" }}>Updated</em>
        </h2>

        <p
          className="text-center mt-4"
          style={{ color: "#B8B2A7", fontSize: 14, lineHeight: 1.6 }}
        >
          We have made updates to our Terms of Service and Privacy Policy. Please review and accept the updated terms to continue using Habano.
        </p>

        {version.what_changed ? (
          <div
            data-testid="terms-reaccept-what-changed"
            className="mt-5"
            style={{
              background: "rgba(232,184,75,0.08)",
              border: "1px solid rgba(232,184,75,0.4)",
              borderRadius: 10,
              padding: "12px 14px",
            }}
          >
            <div style={{ color: "#E8B84B", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 6 }}>
              What changed
            </div>
            <div style={{ color: "#F5F1E8", fontSize: 13, lineHeight: 1.55 }}>
              {version.what_changed}
            </div>
          </div>
        ) : null}

        <div className="mt-5 flex flex-col items-center gap-2">
          <Link
            to="/terms"
            data-testid="terms-reaccept-read-terms"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#E8B84B", fontSize: 13, fontWeight: 500 }}
          >
            Read full Terms of Service →
          </Link>
          <Link
            to="/privacy"
            data-testid="terms-reaccept-read-privacy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#E8B84B", fontSize: 13, fontWeight: 500 }}
          >
            Read Privacy Policy →
          </Link>
        </div>

        <button
          type="button"
          data-testid="terms-reaccept-agree-btn"
          onClick={accept}
          disabled={busy}
          className="w-full mt-6"
          style={{
            background: "#E8B84B",
            color: "#0B0B0B",
            fontSize: 14,
            fontWeight: 600,
            padding: "14px 18px",
            border: "0 solid transparent",
            borderRadius: 11,
            minHeight: 52,
            cursor: busy ? "not-allowed" : "pointer",
            opacity: busy ? 0.6 : 1,
            fontFamily: "'DM Sans', sans-serif",
            letterSpacing: 0.3,
          }}
        >
          {busy ? "Saving…" : "I Agree — Continue to Habano"}
        </button>
      </div>
    </div>
  );
}
